import { styled } from 'styled-components'

import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'

import { ButtonLink } from '@/buyer-portal/components/ButtonLink'

import { Grid, GridMinMax } from '../ListingGrid/ListingGrid'
import type { MoreFromSellerShellProps } from './MoreFromSellerShellProps'

const Title = styled.h3`
  ${Typography.Title3}
  margin: 0;
  padding: 0;
`

const Container = styled.div`
  margin: 0;
  overflow: hidden;
  padding: 0;
  width: 100%;
`

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 0 1rem;
`

const GridContainer = styled.div<{ $columns: number }>`
  display: flex;
  margin: -1.25rem;
  overflow-x: auto;
  padding: 1.25rem; // Adjust as needed based on shadow spread

  ${Grid} {
    // Note: Should match the columns for Grid, but with the section scrolling horizontally
    // if there isn't enough room for everything to fit instead of wrapping to more rows.
    grid-template-columns:
      ${({ $columns }) =>
        $columns > 1 ? `repeat(${$columns - 1}, ${GridMinMax})` : ``}
      repeat(auto-fill, ${GridMinMax});
  }
`

/**
 * V1 (LDP redesign flag off) presentation for the "More from this seller"
 * module: an untitled-card horizontally-scrolling carousel. Data, providers,
 * tracking and the grid of cards are supplied as `children`.
 */
export const MoreFromSellerSection = ({
  children,
  containerRef,
  isLoading,
  listingCount,
  onBrowseAllClick,
  storefrontUrl,
  total,
}: MoreFromSellerShellProps) => (
  <Container ref={containerRef}>
    <TitleContainer>
      <Title>
        <FormattedMessage id="Auction.title.moreFromThisSeller" />
      </Title>
      {storefrontUrl && (isLoading || total > listingCount + 1) ? (
        <ButtonLink
          appearance="plainTertiary"
          href={storefrontUrl}
          onClick={onBrowseAllClick}
          size="medium"
        >
          <FormattedMessage id="Auction.details.carousel.link" />
        </ButtonLink>
      ) : null}
    </TitleContainer>
    <GridContainer $columns={listingCount || 4}>{children}</GridContainer>
  </Container>
)
